import { Injectable } from '@angular/core';

import { environment } from 'environments/environment';

import { HttpClient } from '@angular/common/http';
import { AngularFireDatabase } from '@angular/fire/database';

import { Observable } from 'rxjs';
import { map, first } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  constructor(
    private authService: AuthService,
    private afdb: AngularFireDatabase,
    private http: HttpClient
  ) { }

  sendDemoMail(email: string, account_id: string) {
    return this.http.post(environment.endPoints.sendusage, { token: this.authService.currentUser.token, email: email, account: account_id })
      .pipe(first()).toPromise();
  }

  getUpdates(): Observable<any[]> {
    return this.afdb.list<any>('email_data/updates').snapshotChanges(['child_added', 'child_changed', 'child_moved', 'child_removed'])
    .pipe(
      map(updatesSnap => {
        return updatesSnap
        .map(uSnap => {
          const update = uSnap.payload.val();
          update.id = uSnap.key;
          return update;
        })
        .sort((a, b) => a.order > b.order ? 1 : -1);
      })
    );
  }

  getBlog(): Observable<any[]> {
    return this.afdb.list<any>('email_data/blog').snapshotChanges(['child_added', 'child_changed', 'child_moved', 'child_removed'])
    .pipe(
      map(blogSnap => {
        return blogSnap
        .map(bSnap => {
          const post = bSnap.payload.val();
          post.id = bSnap.key;
          return post;
        })
        .sort((a, b) => a.order > b.order ? 1 : -1);
      })
    );
  }

  moveUpdate(updateId: string, newOrder: number) {
    return this.afdb.database.ref('email_data/updates').transaction(updates => {
      if (updates) {
        // Convert steps object to array
        const updateList = Object.keys(updates).map(u => {
          const update = updates[u];
          update.id = u;
          return update;
        })
        .filter(u => u.id !== updateId)
        .sort((a, b) => a.order - b.order);

        updateList.splice(newOrder, 0, updates[updateId]);
        updateList.forEach((update, index) => {
          update.order = index;
          update.id = null;
        });
      }
      return updates;
    }, () => {}, false);
  }

  movePost(postId: string, newOrder: number) {
    return this.afdb.database.ref('email_data/blog').transaction(blog => {
      if (blog) {
        // Convert steps object to array
        const postList = Object.keys(blog).map(p => {
          const post = blog[p];
          post.id = p;
          return post;
        })
        .filter(p => p.id !== postId)
        .sort((a, b) => a.order - b.order);

        postList.splice(newOrder, 0, blog[postId]);
        postList.forEach((post, index) => {
          post.order = index;
          post.id = null;
        });
      }
      return blog;
    }, () => {}, false);
  }

  changeUpdate(update: any, newData: any) {
    return this.afdb.database.ref(`email_data/updates/${update.id}`).update(newData);
  }

  changePost(post: any, newData: any) {
    return this.afdb.database.ref(`email_data/blog/${post.id}`).update(newData);
  }

  async createUpdate(update: any) {
    const updateId = this.afdb.createPushId();
    const result = await this.afdb.database.ref('email_data/updates').transaction(updates => {
      if (updates) {
        const updateList = Object.keys(updates).map(u => {
          const update = updates[u];
          update.id = u;
          return update;
        });
        let count: number = 0;
        updateList.sort((a, b) => a.order - b.order).forEach(update => {
          updates[update.id].order = count;
          updates[update.id].id = null;
          count = count + 1;
        });
        update.order = count;
        updates[updateId] = update;
      } else {
        update.order = 0;
        return { [updateId]: update }
      }
      return updates;
    }, () => {}, false);
    if (!result.committed) {
      throw new Error('create-update-failed');
    }
    return updateId;
  }

  async createPost(newPost: any) {
    const postId = this.afdb.createPushId();
    const result = await this.afdb.database.ref('email_data/blog').transaction(blog => {
      if (blog) {
        const postList = Object.keys(blog).map(p => {
          const post = blog[p];
          post.id = p;
          return post;
        });
        let count: number = 0;
        postList.sort((a, b) => a.order - b.order).forEach(post => {
          blog[post.id].order = count;
          blog[post.id].id = null;
          count = count + 1;
        });
        newPost.order = count;
        blog[postId] = newPost;
      } else {
        newPost.order = 0;
        return { [postId]: newPost }
      }
      return blog;
    }, () => {}, false);
    if (!result.committed) {
      throw new Error('create-post-failed');
    }
    return postId;
  }

  deleteUpdate(updateId: string) {
    return (this.afdb.database.ref('email_data/updates').transaction(updates => {
      if (updates) {
        const updateList = Object.keys(updates).filter(k => k !== updateId).map(u => {
          const update = updates[u];
          update.id = u;
          return update;
        });
        let count: number = 0;
        updateList.sort((a, b) => a.order - b.order).forEach(update => {
          updates[update.id].order = count;
          updates[update.id].id = null;
          count = count + 1;
        });
        updates[updateId] = null;
      }
      return updates;
    }, () => {}, false)
    .then(result => {
      return this.afdb.database.ref(`email_data/updates/${updateId}`)
      .remove().then(() => result);
    }));
  }

  deletePost(postId: string) {
    return (this.afdb.database.ref('email_data/blog').transaction(blog => {
      if (blog) {
        const postList = Object.keys(blog).filter(k => k !== postId).map(p => {
          const post = blog[p];
          post.id = p;
          return post;
        });
        let count: number = 0;
        postList.sort((a, b) => a.order - b.order).forEach(post => {
          blog[post.id].order = count;
          blog[post.id].id = null;
          count = count + 1;
        });
        blog[postId] = null;
      }
      return blog;
    }, () => {}, false)
    .then(result => {
      return this.afdb.database.ref(`email_data/blog/${postId}`)
      .remove().then(() => result);
    }));
  }
}
